// =============================================================================
// PredicateEval — pure port of Cards/PredicateEval.cs
// Minimal predicate DSL for Phase 3.1b card effects.
//
// COUNT predicates (return number):
//   "countOwnedRenewable"   → seat's renewable facility count
//   "countOwnedFossil"      → seat's fossil facility count
//   "countOwnedOil"         → seat's Oil-type facility count
//
// BOOLEAN predicates (return bool, used by Conditional gating):
//   "ownedRenewable>=3", "ownedFossil>3", "ownedOil==N", etc.
//
// No BoardPlayer dependency in web: caller supplies a CountProvider.
// In tests, use buildTestCountProvider (InjectedCountProvider semantics).
// =============================================================================

import type { FacilityCategory, FacilityType } from "@gdg/shared";

// ---------------------------------------------------------------------------
// PredicateEvalError — typed error (mirrors Unity ArgumentException vs InvalidOperationException)
// ---------------------------------------------------------------------------

/**
 * @TODO(WEB.6) — when errors cross a WebSocket boundary, wrap in an
 * envelope: { name, message, code, stack? }. JSON.stringify(err) drops
 * Error props by default. Serialization-safe transport is WEB.6's protocol.
 */
export class PredicateEvalError extends Error {
  constructor(
    message: string,
    public readonly code: "EmptyPredicate" | "UnknownPredicate" | "UnparseablePredicate",
    public readonly predicate: string,
  ) {
    super(message);
    this.name = "PredicateEvalError";
  }
}

// ---------------------------------------------------------------------------
// CountProvider — abstract interface accepted by evaluateCount / evaluateBool
// ---------------------------------------------------------------------------

/**
 * Abstract count provider. Two implementations:
 *   - Production: FacilityCountProvider — derives counts from real GameState facilities.
 *   - Test:       InjectedCountProvider — returns directly injected key→count values.
 *
 * evaluateCount / evaluateBool only call these three methods.
 */
export interface CountProvider {
  /** Returns the number of facilities of the given category owned by the seat. */
  getOwnedByCategory(seat: number, category: FacilityCategory): number;
  /** Returns the number of facilities of the given type owned by the seat. */
  getOwnedByType(seat: number, facilityType: FacilityType): number;
  /**
   * WEB.4.D — Returns total facility count for the seat across ALL categories.
   * Used by countAllFacilities predicate (Grid_Blackout E2/E4: "LOSE N ENERGY PER FACILITY").
   * Production: sum of all OwnedFacility entries whose ownerSeat === seat.
   * Test: injected under key "all" (see buildTestCountProvider).
   */
  getTotalOwned(seat: number): number;
}

// ---------------------------------------------------------------------------
// InjectedCountProvider — test-only, mirrors _TEST_SetCount / _TEST_Reset
// Returns EXACTLY the injected value for each key. No synthesis from lists.
// ---------------------------------------------------------------------------

/**
 * Builds a CountProvider from injected key→count pairs (for testing).
 * Keys are lowercase category names ("renewable", "fossil") or type names ("oil", "coal", etc.).
 * Lookups return the injected value directly — no overlap, no inference.
 *
 * Example: { fossil: 0, oil: 2 }
 *   getOwnedByCategory(seat, "Fossil") → 0   (exact injection)
 *   getOwnedByType(seat, "Oil")        → 2   (exact injection)
 */
export function buildTestCountProvider(
  counts: ReadonlyArray<{ seat: number; key: string; value: number }>,
): CountProvider {
  // Build a Map<seat, Map<lowercaseKey, value>>
  const store = new Map<number, Map<string, number>>();
  for (const entry of counts) {
    let seatMap = store.get(entry.seat);
    if (seatMap === undefined) {
      seatMap = new Map<string, number>();
      store.set(entry.seat, seatMap);
    }
    seatMap.set(entry.key.toLowerCase(), entry.value);
  }

  return {
    getOwnedByCategory(seat: number, category: FacilityCategory): number {
      const seatMap = store.get(seat);
      if (seatMap === undefined) return 0;
      return seatMap.get(category.toLowerCase()) ?? 0;
    },
    getOwnedByType(seat: number, facilityType: FacilityType): number {
      const seatMap = store.get(seat);
      if (seatMap === undefined) return 0;
      return seatMap.get(facilityType.toLowerCase()) ?? 0;
    },
    /**
     * WEB.4.D: injected via key "all". If not present, falls back to summing
     * the five known FacilityCategory counts (Fossil, Renewable, Nuclear,
     * Geothermal, Waste) so tests that only inject category-level counts still
     * get a correct total without explicitly injecting "all".
     */
    getTotalOwned(seat: number): number {
      const seatMap = store.get(seat);
      if (seatMap === undefined) return 0;
      if (seatMap.has("all")) return seatMap.get("all") ?? 0;
      // Fallback: sum all category-level injections.
      const categories: FacilityCategory[] = [
        "Fossil",
        "Renewable",
        "Nuclear",
        "Geothermal",
        "Waste",
      ];
      return categories.reduce((sum, cat) => sum + (seatMap.get(cat.toLowerCase()) ?? 0), 0);
    },
  };
}

// ---------------------------------------------------------------------------
// Core count functions — delegate to CountProvider methods
// ---------------------------------------------------------------------------

export function countOwnedByCategory(
  seat: number,
  cat: FacilityCategory,
  provider: CountProvider,
): number {
  return provider.getOwnedByCategory(seat, cat);
}

export function countOwnedByType(seat: number, ft: FacilityType, provider: CountProvider): number {
  return provider.getOwnedByType(seat, ft);
}

// ---------------------------------------------------------------------------
// EvaluateCount — count-style predicate → number. Throws on unknown.
// ---------------------------------------------------------------------------

export function evaluateCount(seat: number, predicate: string, provider: CountProvider): number {
  if (!predicate) throw new PredicateEvalError("Empty predicate", "EmptyPredicate", predicate);

  switch (predicate) {
    case "countOwnedRenewable":
      return provider.getOwnedByCategory(seat, "Renewable");
    case "countOwnedFossil":
      return provider.getOwnedByCategory(seat, "Fossil");
    case "countOwnedOil":
      return provider.getOwnedByType(seat, "Oil");
    case "countOwnedNuclear":
      return provider.getOwnedByCategory(seat, "Nuclear");
    case "countAllFacilities":
      // WEB.4.D — Grid_Blackout E2/E4: "LOSE N ENERGY PER FACILITY YOU OWN".
      // Returns total owned facility count across all categories for the seat.
      return provider.getTotalOwned(seat);
    default:
      throw new PredicateEvalError(
        `[PredicateEval] Unknown count predicate: '${predicate}'`,
        "UnknownPredicate",
        predicate,
      );
  }
}

// ---------------------------------------------------------------------------
// EvaluateBool — comparator predicate → boolean. Throws on unknown/unparseable.
// ---------------------------------------------------------------------------

const BOOL_PREDICATE_RE = /^([a-zA-Z]+)\s*(>=|<=|==|!=|>|<)\s*(-?\d+)$/;

export function evaluateBool(seat: number, predicate: string, provider: CountProvider): boolean {
  if (!predicate) throw new PredicateEvalError("Empty predicate", "EmptyPredicate", predicate);

  // WEB.4.D — Named boolean predicates (no comparator — evaluated before regex).
  // Used by Green_Bills_Enact: majority-fossil or majority-renewable branch gate.
  //   majorityFossil    → true if owned Fossil > owned Renewable for the seat.
  //   majorityRenewable → true if owned Renewable > owned Fossil for the seat.
  // Both return false on ties (strictly greater-than). These are gating predicates
  // (isGatingPredicate returns true for named bool predicates).
  if (predicate === "majorityFossil") {
    const fossil = provider.getOwnedByCategory(seat, "Fossil");
    const renewable = provider.getOwnedByCategory(seat, "Renewable");
    return fossil > renewable;
  }
  if (predicate === "majorityRenewable") {
    const renewable = provider.getOwnedByCategory(seat, "Renewable");
    const fossil = provider.getOwnedByCategory(seat, "Fossil");
    return renewable > fossil;
  }

  const m = BOOL_PREDICATE_RE.exec(predicate);
  if (m === null) {
    throw new PredicateEvalError(
      `[PredicateEval] Unparseable bool predicate: '${predicate}'`,
      "UnparseablePredicate",
      predicate,
    );
  }

  const lhsKey = m[1] ?? "";
  const op = m[2] ?? "";
  const rhs = Number.parseInt(m[3] ?? "0", 10);

  let lhs: number;
  switch (lhsKey) {
    case "ownedRenewable":
      lhs = provider.getOwnedByCategory(seat, "Renewable");
      break;
    case "ownedFossil":
      lhs = provider.getOwnedByCategory(seat, "Fossil");
      break;
    case "ownedOil":
      lhs = provider.getOwnedByType(seat, "Oil");
      break;
    case "ownedNuclear":
      lhs = provider.getOwnedByCategory(seat, "Nuclear");
      break;
    case "ownedSolar":
      // Codex WEB.4.C R1 HIGH: Perovskite_Solar_Breakthrough DrawCard arm
      // gates on ownedSolar>3. FacilityType-level predicate (Solar IS a
      // FacilityType, not a Category — Renewable category aggregates all).
      lhs = provider.getOwnedByType(seat, "Solar");
      break;
    default:
      throw new PredicateEvalError(
        `[PredicateEval] Unknown lhs key: '${lhsKey}'`,
        "UnknownPredicate",
        predicate,
      );
  }

  switch (op) {
    case ">=":
      return lhs >= rhs;
    case "<=":
      return lhs <= rhs;
    case "==":
      return lhs === rhs;
    case "!=":
      return lhs !== rhs;
    case ">":
      return lhs > rhs;
    case "<":
      return lhs < rhs;
    default:
      throw new PredicateEvalError(
        `[PredicateEval] Unknown op: '${op}'`,
        "UnparseablePredicate",
        predicate,
      );
  }
}

// ---------------------------------------------------------------------------
// IsGatingPredicate / IsCountPredicate — classification helpers
// ---------------------------------------------------------------------------

/**
 * True if this predicate string gates the effect (comparator OR named bool predicate).
 * WEB.4.D: "majorityFossil" and "majorityRenewable" are named bool predicates
 * (no comparator in the string) — must be explicitly listed here so the pipeline
 * recognises them as gating predicates and evaluates them via evaluateBool.
 */
export function isGatingPredicate(predicate: string): boolean {
  if (!predicate) return false;
  // Named boolean predicates (WEB.4.D).
  if (predicate === "majorityFossil" || predicate === "majorityRenewable") return true;
  // Comparator-based predicates (pre-WEB.4.D).
  return (
    predicate.includes(">=") ||
    predicate.includes("<=") ||
    predicate.includes("==") ||
    predicate.includes("!=") ||
    predicate.includes(">") ||
    predicate.includes("<")
  );
}

/**
 * True if this predicate string scales the effect by an owned count.
 * WEB.4.D: "countAllFacilities" follows the existing "countOwned" naming convention.
 */
export function isCountPredicate(predicate: string): boolean {
  if (!predicate) return false;
  return predicate.startsWith("countOwned") || predicate === "countAllFacilities";
}
